import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useAppSelector } from '@app/hooks';

import { selectFilter } from '@dashboard/slices/filters';
import {
  CHART_DEFAULT_STROKE_WIDTH,
  CHART_CURSOR_COLOR,
  CHARTS_BOUNDS,
  getChartMetadata,
  CHART_GLOBAL_AXIS_INTERVAL,
  CHART_GLOBAL_ALLOW_DECIMALS,
} from '@dashboard/constants';
import { makeUID } from '@dashboard/util';

function ChartComponentType(type) {
  if (type === 'bar' || type === 'bar-stacked') return BarChart;
  if (type === 'line') return LineChart;
  return BarChart;
}

function ChartItemType(type) {
  if (type === 'bar' || type === 'bar-stacked') return Bar;
  if (type === 'line') return Line;
  return Bar;
}

export default function ChartFactory({ data, type }) {
  const currentFilters = useAppSelector(selectFilter);
  const [dataState, setDataState] = useState(null);
  const [heightAmount, setHeightAmount] = useState(0);

  const [meta] = useState(getChartMetadata(data.name, currentFilters));
  const ChartComponent = ChartComponentType(type);
  const ChartItem = ChartItemType(type);

  const { t } = useTranslation(
    meta && meta.translation ? meta.translation : '',
  );

  const legendFormatter = (value) => (
    <span className="text-color-class">{t(value)}</span>
  );

  useEffect(() => {
    if (meta && data) {
      const dataSet =
        meta && meta.parse ? meta.parse(data, t, meta) : data.dates;
      setDataState(dataSet);

      const totalHeight =
        meta.x_axis && meta.x_axis.length > 0
          ? meta.x_axis.reduce((accumulator, currentValue) => {
              return currentValue.height !== undefined
                ? accumulator + currentValue.height
                : accumulator;
            }, 0)
          : 0;

      setHeightAmount(CHARTS_BOUNDS.height + totalHeight);
    }
  }, [data, meta]);

  return (
    dataState &&
    meta && (
      <ResponsiveContainer width="100%" height={heightAmount}>
        <ChartComponent
          layout={meta.layout}
          width={CHARTS_BOUNDS.width}
          height={heightAmount}
          data={dataState}
          margin={CHARTS_BOUNDS.margin}
        >
          <CartesianGrid
            key={`grid_${makeUID()}`}
            strokeDasharray="3 3"
            horizontal={
              meta.horizontal === true || meta.horizontal === undefined
            }
          />
          {meta.x_axis?.map((axis, index) => (
            <XAxis
              key={`x_axis_${index}_${makeUID()}`}
              {...{
                label: {
                  angle: CHARTS_BOUNDS.x_axis_angle,
                  dx: CHARTS_BOUNDS.x_axis_dx,
                },
                angle: CHARTS_BOUNDS.x_axis_angle,
                textAnchor: 'end',
                scaleToFit: 'true',
                interval: axis.interval || CHART_GLOBAL_AXIS_INTERVAL,
                allowDecimals:
                  axis.allowDecimals || CHART_GLOBAL_ALLOW_DECIMALS,
              }}
              {...axis}
              {...{
                label:
                  axis.label && axis.label.value
                    ? {
                        angle: CHARTS_BOUNDS.x_axis_label_angle,
                        dy: CHARTS_BOUNDS.x_axis_dy,
                        ...axis.label,
                        value: t(axis.label.value),
                      }
                    : { ...axis.label },
                tickFormatter: axis.tickFormatter
                  ? (value) => axis.tickFormatter(value, t)
                  : undefined,
              }}
            />
          ))}

          {meta.y_axis?.map((axis, index) => (
            <YAxis
              key={`y_axis_${index}_${makeUID()}`}
              {...{
                label: {
                  angle: CHARTS_BOUNDS.y_axis_angle,
                  dx: CHARTS_BOUNDS.y_axis_dx,
                },
                textAnchor: 'end',
                scaleToFit: 'true',
                allowDecimals:
                  axis.allowDecimals || CHART_GLOBAL_ALLOW_DECIMALS,
                interval: axis.interval || CHART_GLOBAL_AXIS_INTERVAL,
                tickFormatter: axis.tickFormatter
                  ? (value) => axis.tickFormatter(value, t)
                  : undefined,
              }}
              {...axis}
              {...{
                label:
                  axis.label && axis.label.value
                    ? {
                        angle: CHARTS_BOUNDS.y_axis_angle,
                        dx: CHARTS_BOUNDS.y_axis_dx,
                        ...axis.label,
                        value: t(axis.label.value),
                      }
                    : { ...axis.label },
                tickFormatter: axis.tickFormatter
                  ? (value) => axis.tickFormatter(value, t)
                  : undefined,
              }}
            />
          ))}

          <Tooltip
            cursor={{ fill: CHART_CURSOR_COLOR }}
            formatter={meta.tooltipFormatter}
          />
          {(meta.y_keys || meta.x_keys).map((k) => (
            <ChartItem
              type={meta.type}
              key={`key_${k.label}_${makeUID()}`}
              dataKey={t(k.label)}
              stackId={k.stackId}
              fill={k.color}
              stroke={k.stroke}
              barSize={k.width}
              legendType={k.legendType}
              strokeWidth={
                meta.strokeWidth ? meta.strokeWidth : CHART_DEFAULT_STROKE_WIDTH
              }
            />
          ))}
          <Legend
            height={CHARTS_BOUNDS.legend_height}
            verticalAlign="top"
            formatter={legendFormatter}
          />
        </ChartComponent>
      </ResponsiveContainer>
    )
  );
}

ChartFactory.propTypes = {
  data: PropTypes.object,
  type: PropTypes.string,
};
